/**
 * CollapseToggle — small indigo pill button shared by surfaces that
 * collapse/expand a section (search bar, advanced filters, etc).
 *
 * Default placement: center-justified, half-rounded-bottom so it straddles
 * a section's bottom border. The arrow rotates based on `collapsed`:
 *   - collapsed=false → chevron up (click to collapse)
 *   - collapsed=true  → chevron down (click to expand)
 *
 * Numeo DS notes:
 *   - Uses the DS primary brand color (`blue-600` / `blue-700`), same as the
 *     <Button variant="primary"> + SendButton tokens. Indigo was used by the
 *     pre-DS main build; this is now aligned with the rest of the design system.
 *   - `rounded-b-md` for the straddling placement; pass `className` to swap
 *     to `rounded-full` or `rounded-md` when used standalone.
 *
 * Optional `position` prop is reserved for future placements (top-edge,
 * standalone). Today only `bottom-edge` is in use.
 */

export interface CollapseToggleProps {
    /** True when the panel below is hidden — arrow points DOWN (expand). */
    collapsed: boolean
    onClick: () => void
    /** Optional override for the className (placement / shape). Defaults to a
     *  bottom-edge half-rounded pill matching the search-form usage. */
    className?: string
    /** Title / aria-label override. Defaults derive from `collapsed`. */
    title?: string
    'aria-label'?: string
}

const DEFAULT_CLASS =
    'flex items-center justify-center w-10 h-5 rounded-b-md bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 shadow-sm hover:shadow transition-all'

export const CollapseToggle = ({ collapsed, onClick, className = DEFAULT_CLASS, title, 'aria-label': ariaLabel }: CollapseToggleProps) => {
    const label = collapsed ? 'Expand' : 'Collapse'
    return (
        <button type="button" onClick={onClick} title={title ?? label} aria-label={ariaLabel ?? label} className={className}>
            <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={collapsed ? 'M19 9l-7 7-7-7' : 'M5 15l7-7 7 7'} />
            </svg>
        </button>
    )
}
